import { memo, useMemo } from 'react'
import { AppointmentNoteAddendum, SourceDocument } from '../../types/types'
import {
  formatAddendumEditor,
  summariseAmendment,
} from '../../utils/appointment-note-addendum.utils'
import { formatMMDDYYYY, formatTime } from '../../utils/time.utils'
import NoteChangeSummary from './NoteChangeSummary'
import './styles.scss'

type NoteEditHistoryProps = {
  addenda: AppointmentNoteAddendum[]
  author: string
  submittedAt: string
  /** The files the note was signed with. */
  originalAttachments?: SourceDocument[]
  isLoading: boolean
}

/**
 * Removed files are only reachable from here, so each entry links the attachments that
 * were active when it was saved. A missing link renders the name alone.
 */
const AttachmentLinks: React.FC<{ files?: SourceDocument[] }> = ({ files }) =>
  files?.length ? (
    <p className="history-attachments mb-0">
      <span className="history-attachments-label">Active attachments: </span>
      {files.map((file, index) => (
        <span key={file.id}>
          {index > 0 && ', '}
          {file.fileUrl ? (
            <a href={file.fileUrl} target="_blank" rel="noreferrer">
              {file.fileName}
            </a>
          ) : (
            file.fileName
          )}
        </span>
      ))}
    </p>
  ) : null

/**
 * Read-only audit trail for a signed note, in the order the server returns it.
 * Renders for any signed note, edited or not, because the provenance line
 * belongs to the note rather than to the edits.
 */
const NoteEditHistory: React.FC<NoteEditHistoryProps> = ({
  addenda,
  author,
  submittedAt,
  originalAttachments,
  isLoading,
}) => {
  // A removed file is in no entry's active list after its removal, but an earlier
  // entry or the signed note still carries its link.
  const fileUrls = useMemo(() => {
    const urls: Record<string, string> = {}
    ;[
      ...(originalAttachments ?? []),
      ...addenda.flatMap((a) => a.attachments ?? []),
    ]
      .filter((file) => file.fileUrl)
      .forEach((file) => {
        urls[file.id] = file.fileUrl as string
      })
    return urls
  }, [addenda, originalAttachments])

  return (
    <div className="note-edit-history w-100">
      <div className="history-header d-flex justify-content-between align-items-baseline">
        <span className="history-title">Edit history</span>
        <span className="edit-count">
          {/* An in-flight fetch yields [], and "0 edits" under a banner saying the
            edit is logged below reads as data loss. */}
          {isLoading
            ? 'Loading…'
            : `${addenda.length} ${addenda.length === 1 ? 'edit' : 'edits'}`}
        </span>
      </div>

      {addenda.map((amendment, index) => {
        const editedAt = new Date(amendment.createdAt)

        return (
          <div className="entry" key={amendment.id ?? index}>
            <div className="entry-header d-flex justify-content-between gap-2">
              <span className="editor">
                Edited by {formatAddendumEditor(amendment)}
              </span>
              <span className="when">
                {formatMMDDYYYY(editedAt)} {formatTime(editedAt)}
              </span>
            </div>
            {amendment.comment && (
              <p className="reason mb-0">
                <span className="reason-label">Reason: </span>
                {amendment.comment}
              </p>
            )}
            <NoteChangeSummary
              changes={summariseAmendment(amendment)}
              fileUrls={fileUrls}
            />
            <AttachmentLinks files={amendment.attachments} />
          </div>
        )
      })}

      <p className="note-provenance mb-0">
        {`Original note submitted by ${author} · ${submittedAt}`}
      </p>
      <AttachmentLinks files={originalAttachments} />
    </div>
  )
}

export default memo(NoteEditHistory)
